import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import Select from "react-select";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import {
  incomeSourceOptions,
  paymentStatusOption,
} from "../../../components/Data/DropDownData";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

const IncomeUpdateChild = ({ incomeRecord }) => {
  dayjs.extend(utc);
  let navigate = useNavigate();
  const style = {
    control: (base) => ({
      ...base,
      border: "1px solid lightgray",
      boxShadow: "none",
    }),
  };

  const [{ errorLogApi, placeHolderText, user }] = useAtom(gbState);
  const [churchMembers, setChurchMembers] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [useOtherNonMember, setUseOtherNonMember] = useState(
    !!incomeRecord?.other_non_member
  );
  const [formData, setFormData] = useState({
    category_id: incomeRecord?.category_id?._id,
    income_title: incomeRecord?.income_title,
    description: incomeRecord?.description,
    member_id: incomeRecord?.member_id?._id,
    date: incomeRecord?.date,
    amount: incomeRecord?.amount,
    income_source: incomeRecord?.income_source,
    status: incomeRecord?.status,
    other_non_member: incomeRecord?.other_non_member,
  });

  useEffect(() => {
    const getDataFromServer = () => {
      let userToken = localStorage.getItem("token");

      let urlLink = `${process.env.REACT_APP_API_URL}/category/income/${user.church_id}`;

      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };
      axios(config)
        .then((response) => {
          const oldData = response.data.Categorys;
          const updatedData = oldData.map((data) => ({
            value: data._id,
            label: data.category_name,
          }));
          setCategoryList(updatedData);
        })
        .catch((err) => {
          console.log("error api call ", err.response.data.message);
        });
    };
    getDataFromServer();
  }, []);

  useEffect(() => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/user/church/${user?.church_id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios(config)
      .then((response) => {
        const members = response.data.users;
        setChurchMembers(
          members.map((member) => ({
            value: member._id,
            label: member.first_name + " " + member.last_name,
          }))
        );
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
        errorLogApi(error, "IncomeList", "/income");
      });
  }, []);

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const handleCheckboxChange = () => {
    setUseOtherNonMember(!useOtherNonMember);
    setFormData({
      ...formData,
      member_id: useOtherNonMember ? null : formData.member_id,
      other_non_member: useOtherNonMember ? "" : null,
    });
  };

  const handleUpdateIncome = (e) => {
    e.preventDefault();

    if (!formData?.income_source)
      return toast.error("Please select an income source");

    if (!formData?.status) return toast.error("Please select payment status");

    if (!formData?.amount) return toast.error("Please select payment amount");

    let userToken = localStorage.getItem("token");

    const config = {
      method: "patch",
      url: `${process.env.REACT_APP_API_URL}/income/update/${incomeRecord?._id}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: { ...formData },
    };
    axios(config)
      .then((response) => {
        toast.success("Receipt Updated Successfully.");
        navigate("/income");
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message);
        errorLogApi(error, "IncomeUpdateChild", "/income/update");
      });
  };

  return (
    <div className="flex justify-center w-full min-h-screen bg-gray-50">
      <div className="p-8 w-full max-w-2xl">
        <form
          onSubmit={(e) => handleUpdateIncome(e)}
          autoComplete="off"
          className="p-6 bg-white rounded-lg shadow-md"
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Update Receipt</h2>
            <div className="space-x-2">
              <button
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md transition duration-300 ease-in-out hover:bg-gray-300"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-white bg-blue-600 rounded-md transition duration-300 ease-in-out hover:bg-blue-700"
                type="submit"
              >
                Update
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Category
              </label>
              <Select
                name="category_id"
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    category_id: selected.value,
                  });
                }}
                options={categoryList}
                placeholder={placeHolderText("Select Category")}
                className="w-full"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#D1D5DB",
                    borderRadius: "0.375rem",
                    "&:hover": { borderColor: "#9CA3AF" },
                  }),
                }}
                value={
                  formData?.category_id &&
                  categoryList?.find((c) => c.value === formData?.category_id)
                }
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Income Title
              </label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                <input
                  className="p-3 pl-10 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  name="income_title"
                  type="text"
                  required
                  placeholder="Income Title"
                  onChange={handleChange}
                  value={formData?.income_title}
                />
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Date
              </label>
              <div className="relative">
                <DatePicker
                  className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  name="date"
                  onChange={(date, dateString) => {
                    setFormData({
                      ...formData,
                      date: dateString,
                    });
                  }}
                  showToday={true}
                  format="MM-DD-YYYY"
                  value={formData?.date ? dayjs.utc(formData?.date) : null}
                />
                <CalendarIcon className="absolute right-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
              </div>
            </div>

            <div>
              <div className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={useOtherNonMember}
                  onChange={handleCheckboxChange}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Other Non Member
                </label>
              </div>
              {!useOtherNonMember ? (
                <Select
                  name="member_id"
                  onChange={(selected) => {
                    setFormData({
                      ...formData,
                      member_id: selected?.value,
                      other_non_member: null,
                    });
                  }}
                  options={churchMembers}
                  placeholder={placeHolderText("Select Member")}
                  className="w-full"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#D1D5DB",
                      borderRadius: "0.375rem",
                      "&:hover": { borderColor: "#9CA3AF" },
                    }),
                  }}
                  value={
                    formData?.member_id &&
                    churchMembers?.find((m) => m?.value === formData?.member_id)
                  }
                  isClearable
                />
              ) : (
                <div className="relative">
                  <input
                    className="p-3 pl-10 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    name="other_non_member"
                    type="text"
                    required
                    placeholder="Enter Non Member"
                    onChange={handleChange}
                    value={formData?.other_non_member}
                  />
                  <UserIcon className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                </div>
              )}
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Amount
              </label>
              <div className="relative">
                <CurrencyDollarIcon className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
                <input
                  className="p-3 pl-10 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  name="amount"
                  type="number"
                  required
                  placeholder="Enter Amount"
                  onChange={handleChange}
                  value={formData?.amount}
                />
              </div>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Income Source
              </label>
              <Select
                name="income_source"
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    income_source: selected.label,
                  });
                }}
                options={incomeSourceOptions}
                placeholder={placeHolderText("Select Source")}
                className="w-full"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#D1D5DB",
                    borderRadius: "0.375rem",
                    "&:hover": { borderColor: "#9CA3AF" },
                  }),
                }}
                value={
                  formData?.income_source &&
                  incomeSourceOptions?.find(
                    (i) => i.label === formData?.income_source
                  )
                }
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Status
              </label>
              <Select
                name="status"
                onChange={(selected) => {
                  setFormData({
                    ...formData,
                    status: selected.value,
                  });
                }}
                options={paymentStatusOption}
                placeholder={placeHolderText("Select Status")}
                className="w-full"
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: "#D1D5DB",
                    borderRadius: "0.375rem",
                    "&:hover": { borderColor: "#9CA3AF" },
                  }),
                }}
                value={
                  formData?.status &&
                  paymentStatusOption.find(
                    (p) => p.value === parseInt(formData?.status)
                  )
                }
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="relative">
                <textarea
                  className="p-3 pl-10 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  name="description"
                  rows={3}
                  placeholder="Income Description"
                  onChange={handleChange}
                  value={formData?.description}
                />
                <DocumentTextIcon className="absolute top-3 left-3 w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncomeUpdateChild;
